import React, { useState } from "react";
import { Copy, ExternalLink, MessageCircle, Phone, MessageSquareText } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { WhatsAppLogo } from "../../components/home/ContactStrip";
import { copyText, useFb, SITE } from "./fb/shared";
import { QrSection } from "./QrSection";
import { FlyerSection } from "./FlyerSection";

const MSG = "Hi StripWise, I'd like a free quote";

const Row = ({ label, value, note, testid }) => (
  <div className="flex flex-wrap items-center gap-2 border-b border-[#eef5f3] py-3 last:border-0" data-testid={testid}>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-semibold text-[#3d504d]">{label}</div>
      <code className="block break-all text-[14px] text-[#0f2e2a]">{value}</code>
      {note && <div className="text-[12px] text-[#8a9a97]">{note}</div>}
    </div>
    <button onClick={() => copyText(value, "Link copied")} className="inline-flex items-center gap-1.5 rounded-full bg-[#0e8c7f] px-4 py-2 text-[13px] font-bold text-white hover:bg-[#0b6f65]" data-testid={`${testid}-copy`}><Copy size={14} />Copy</button>
    {value.startsWith("http") && <a href={value} target="_blank" rel="noreferrer" className="rounded-full border border-[#cfe3df] p-2 text-[#0e8c7f]" aria-label="Open" data-testid={`${testid}-open`}><ExternalLink size={15} /></a>}
  </div>
);

const Group = ({ icon, title, color, children, testid }) => (
  <div className="rounded-2xl border border-[#e3ecea] bg-white p-4 md:p-5" data-testid={testid}>
    <div className="mb-1 flex items-center gap-2"><span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ background: color }}>{icon}</span><h3 className="text-[17px] font-bold text-[#0f2e2a]">{title}</h3></div>
    {children}
  </div>
);

export default function LinksTab() {
  const { rawContent, content } = useSite();
  const c = rawContent || content || {};
  const pages = useFb("pages").items;
  const [msg, setMsg] = useState(MSG);
  const enc = encodeURIComponent(msg);
  const wa = c.whatsapp_raw || "", tel = c.phone_raw || "";
  return (
    <div className="max-w-4xl space-y-4" data-testid="links-tab">
      <QrSection />
      <FlyerSection />
      <label className="block rounded-2xl border border-[#e3ecea] bg-white p-4">
        <span className="mb-1 block text-[12px] font-semibold uppercase text-[#6b7c79]">Pre-typed message for WhatsApp & text links</span>
        <input value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full rounded-lg border border-[#dcebe8] px-3 py-2 text-[14px]" data-testid="links-message" />
      </label>
      <Group icon={<MessageCircle size={18} />} title="Live chat" color="#0e8c7f" testid="links-chat">
        <Row label="Facebook posts" value={`${SITE}/chat?src=facebook`} testid="link-chat-facebook" />
        {pages.map((p) => <Row key={p.id} label={`Page: ${p.name}`} value={`${SITE}/chat?src=fb_${p.slug}`} note="Counts chats for this page in FB Pages" testid={`link-chat-${p.slug}`} />)}
        <Row label="General" value={`${SITE}/chat`} testid="link-chat-general" />
      </Group>
      <Group icon={<WhatsAppLogo size={18} />} title={`WhatsApp ${c.whatsapp_display || ""}`} color="#25D366" testid="links-whatsapp">
        <Row label="Plain" value={`https://wa.me/${wa}`} testid="link-wa-plain" />
        <Row label="With message" value={`https://wa.me/${wa}?text=${enc}`} testid="link-wa-msg" />
      </Group>
      <Group icon={<MessageSquareText size={18} />} title={`Text message ${c.phone_display || ""}`} color="#2563eb" testid="links-sms">
        <Row label="Plain" value={`sms:${tel}`} note="Works on websites, emails and buttons. In Facebook posts, write the number instead." testid="link-sms-plain" />
        <Row label="With message" value={`sms:${tel}?&body=${enc}`} testid="link-sms-msg" />
        <Row label="For Facebook posts" value={`Text us: ${c.phone_display || tel}`} testid="link-sms-fb" />
      </Group>
      <Group icon={<Phone size={18} />} title="Call" color="#0f2e2a" testid="links-call">
        <Row label="Tap to call" value={`tel:${tel}`} testid="link-tel" />
      </Group>
    </div>
  );
}
